import { Fragment } from 'react';
import type { GameView, PublicStats, RoomView } from '@mt/protocol';
import { useApp } from '../net/client';
import { ACTION_TITLES, cr, crn, secondsLeft } from '../lib/format';
import { num, t } from '../i18n';
import { pct, signedCr, toneOf } from './kit';

/** Открытая статистика игрока на его плашке (если он разрешил её показывать в профиле). */
export function SeatStats({ stats }: { stats: PublicStats }) {
  return (
    <span className="seat-stats" title={t('table.seats.statsTitle', { games: num(stats.games, 0), winRate: pct(stats.winRate), net: signedCr(stats.net) })}>
      <span>WR {pct(stats.winRate)}</span>
      <span className={toneOf(stats.net)}>{signedCr(stats.net)}</span>
    </span>
  );
}

/** Доля оставшегося времени хода, после которой полоса становится тревожной. */
const TURN_WARN_SHARE = 0.3;

/** Участники розыгрыша: видны действия и суммы, но не прогнозы. Одна строка на игрока. */
export function GameSeats({ game, now }: { game: GameView; now: number }) {
  const turnMs = useApp((s) => s.info?.turnMs ?? 12_000);
  return (
    <table className="seat-table">
      <thead>
        <tr>
          <th>{t('table.seats.colPlayer')}</th>
          <th className="num" title={t('table.seats.colRoundHint')}>
            {t('table.seats.colRound')}
          </th>
          <th className="num" title={t('table.seats.colCommittedHint')}>
            {t('table.seats.colCommitted')}
          </th>
          <th className="num" title={t('table.seats.colStackHint')}>
            {t('table.seats.colStack')}
          </th>
        </tr>
      </thead>
      <tbody>
        {game.seats.map((s) => {
          const turn = game.toActPlayerId === s.playerId;
          const left = turn ? secondsLeft(game.turnEndsAt, now) : null;
          let act: string;
          let tone = '';
          if (turn) {
            act = t('table.seats.turn', { s: left === null ? '—' : num(left, 0) });
            tone = 'accent';
          } else if (s.status === 'folded') act = t('common.action.fold');
          else if (s.status === 'allin') {
            act = t('common.action.allin');
            tone = 'accent';
          } else if (s.lastAction)
            act = `${ACTION_TITLES[s.lastAction.type]}${s.lastAction.amount && s.lastAction.type !== 'ante' ? ' ' + cr(s.lastAction.amount) : ''}${s.lastAction.auto ? ' · ' + t('common.auto') : ''}`;
          else act = '';
          return (
            <Fragment key={s.playerId}>
              <tr className={`${s.status} ${turn ? 'turn' : ''} ${s.isMe ? 'me' : ''}`}>
                <td>
                  <div className="who-cell">
                    <span className="who-name">
                      {s.name}
                      {s.isMe && <em className="tag me">{t('table.seats.me')}</em>}
                      {s.isBot && <em className="tag">{t('common.bot')}</em>}
                      {!s.connected && <em className="tag warn">{t('table.seats.offline')}</em>}
                    </span>
                    <span className={`who-act ${tone}`}>{act}</span>
                  {s.stats && <SeatStats stats={s.stats} />}
                  </div>
                </td>
                <td className="num mono">{s.roundBet ? crn(s.roundBet) : '—'}</td>
                <td className="num mono">{crn(s.committed)}</td>
                <td className="num mono">{crn(s.stack)}</td>
              </tr>
              {turn && game.turnEndsAt !== null && <TurnProgress endsAt={game.turnEndsAt} turnMs={turnMs} now={now} me={s.isMe} />}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/** Строка таблицы с полосой времени хода — под строкой ходящего игрока, видна всем за столом. */
function TurnProgress(props: { endsAt: number; turnMs: number; now: number; me: boolean }) {
  return (
    <tr className="turn-progress" aria-hidden="true">
      <td colSpan={4}>
        <TurnBar {...props} />
      </td>
    </tr>
  );
}

/**
 * Полоса оставшегося времени хода. Время берётся с сервера (turnEndsAt по серверным часам),
 * полоса только отображает его; в последние 30 % — тревожный цвет.
 */
export function TurnBar({ endsAt, turnMs, now, me }: { endsAt: number; turnMs: number; now: number; me: boolean }) {
  const share = Math.min(1, Math.max(0, (endsAt - now) / turnMs));
  return (
    <div className={`turn-bar ${me ? 'me' : ''} ${share <= TURN_WARN_SHARE ? 'low' : ''}`} aria-hidden="true">
      <i style={{ transform: `scaleX(${share})` }} />
    </div>
  );
}

/** Места за столом между розыгрышами. */
export function RoomSeats({ room }: { room: RoomView }) {
  const empty = Math.max(0, room.maxPlayers - room.seats.length);
  return (
    <table className="seat-table">
      <thead>
        <tr>
          <th>{t('table.seats.colPlayer')}</th>
          <th>{t('table.seats.colStatus')}</th>
          <th className="num">{t('table.seats.colBalance')}</th>
        </tr>
      </thead>
      <tbody>
        {room.seats.map((s) => (
          <tr key={s.playerId} className={s.isMe ? 'me' : ''}>
            <td>
              <span className="who-name">
                {s.name}
                {s.isMe && <em className="tag me">{t('table.seats.me')}</em>}
                {s.isBot && <em className="tag">{t('common.bot')}</em>}
                {!s.connected && <em className="tag warn">{t('table.seats.offline')}</em>}
              </span>
              {s.stats && <SeatStats stats={s.stats} />}
            </td>
            <td>{s.inGame ? <span className="dim">{t('table.seats.inGame')}</span> : s.ready ? <span className="ok">{t('table.seats.ready')}</span> : <span className="dim">{t('table.seats.waiting')}</span>}</td>
            <td className="num mono">{crn(s.balance)}</td>
          </tr>
        ))}
        {Array.from({ length: empty }, (_, i) => (
          <tr key={'e' + i} className="empty">
            <td colSpan={3}>{t('table.seats.free')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
